import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';
import LanguageToggle from '../components/LanguageToggle';
import PostCard from '../components/PostCard';
import { toast } from 'sonner';

const TABS = ['foryou', 'following'];

export default function FeedPage() {
  const { user, isAdmin } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = useState('foryou');
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [end, setEnd] = useState(false);
  const sentinel = useRef(null);

  const load = useCallback(async (reset = false) => {
    if (reset) { setLoading(true); setEnd(false); }
    else if (end || loadingMore) return;
    else setLoadingMore(true);
    try {
      const params = { tab, limit: 15 };
      if (!reset && cursor) params.cursor = cursor;
      const { data } = await api.get('/feed', { params });
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setCursor(data.next_cursor);
      if (!data.next_cursor) setEnd(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false); setLoadingMore(false);
    }
  }, [tab, cursor, end, loadingMore, t]);

  useEffect(() => { setItems([]); setCursor(null); load(true); /* eslint-disable-next-line */ }, [tab]);

  useEffect(() => {
    if (!sentinel.current) return undefined;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) load(false);
    }, { threshold: 0.1 });
    obs.observe(sentinel.current);
    return () => obs.disconnect();
  }, [load]);

  return (
    <div className="min-h-screen bg-aura-bg pb-28 relative">
      <div className="absolute inset-0 bg-aura-radial pointer-events-none" />

      <header className="sticky top-0 z-20 bg-aura-bg/85 backdrop-blur border-b border-white/5">
        <div className="mx-auto max-w-md px-5 pt-5 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="text-aura-cyan drop-shadow-[0_0_10px_rgba(0,240,255,0.7)]" />
            <span className="font-display font-black text-lg tracking-[0.3em] aura-text-gradient">AURA</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/search" data-testid="link-search" className="p-2 rounded-full border border-white/10 hover:border-aura-cyan/50">
              <Search size={16} className="text-white" />
            </Link>
            {isAdmin && (
              <Link to="/admin" data-testid="link-admin" className="aura-chip border-aura-yellow/40 text-aura-yellow">ADMIN</Link>
            )}
            <LanguageToggle />
          </div>
        </div>
        <div className="mx-auto max-w-md px-5 flex gap-6 pb-2">
          {TABS.map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`feed-tab-${k}`}
              className={`relative pb-2 font-display font-bold text-sm tracking-[0.2em] transition ${tab === k ? 'text-white' : 'text-aura-textDim'}`}
            >
              {t(`feed.tab${k === 'foryou' ? 'ForYou' : 'Following'}`)}
              {tab === k && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-aura-gradient shadow-[0_0_10px_rgba(0,240,255,0.6)]" />}
            </button>
          ))}
        </div>
      </header>

      <main className="relative mx-auto max-w-md px-4 pt-4 space-y-5">
        {loading ? (
          <div className="text-center py-20 text-aura-textDim animate-aura-pulse text-4xl">⚡</div>
        ) : items.length === 0 ? (
          <div className="aura-card p-8 text-center">
            <div className="text-5xl mb-2">⚡</div>
            <div className="text-sm text-aura-textDim">{tab === 'foryou' ? t('feed.emptyForYou') : t('feed.empty')}</div>
          </div>
        ) : (
          items.map((p) => (
            <PostCard
              key={p.post_id}
              post={p}
              onDeleted={(pid) => setItems((prev) => prev.filter((x) => x.post_id !== pid))}
            />
          ))
        )}
        <div ref={sentinel} className="h-8" />
        {loadingMore && <div className="text-center text-aura-textDim py-4 animate-aura-pulse text-2xl">⚡</div>}
      </main>

      <BottomNav />
    </div>
  );
}
